import { Box, chakra } from '@chakra-ui/react';

import { KycReviewDocumentType } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import { OBList } from 'features/Bento/components';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import VerificationDocumentUpload from 'features/Onboarding/legacy/Pages/VerificationAddressUpload/VerificationDocumentUpload';

import Header from '../../../../components/Header';
import { isBeforeSeptember } from '../../helpers/dates';
import { DocumentFile, PoaType } from '../../machine/model';
import locales from './locales';

const HELP_PAGE_URL =
  'https://help.shine.fr/fr/articles/1617824-quels-sont-les-documents-obligatoires-pour-la-creation-de-mon-compte-shine';

interface ProofOfAddressUploadProps {
  isSelfHosted: boolean;
  poaType: PoaType;
  onSuccessfulUpload: (uploadedFile: DocumentFile) => void;
}

/**
 * A page asking the user to upload their proof of address file.
 */
const ProofOfAddressUpload = asBentoPage<ProofOfAddressUploadProps>(
  ({ isSelfHosted, Layout, onPrev, onSuccessfulUpload, poaType }) => {
    const checkContent = isSelfHosted
      ? locales.checkContent.selfHosted
      : locales.checkContent.hosted;

    const checkContentWithoutDate = isSelfHosted
      ? locales.checkContentWithoutDate.selfHosted
      : locales.checkContentWithoutDate.hosted;

    const list: (string | undefined)[] = [
      // In almost all cases we want the notion of 3 months in the first check
      // The only EXCEPTION is for the income tax notice before the 1st of September
      isBeforeSeptember && poaType === 'INCOME_TAX_NOTICE'
        ? checkContentWithoutDate
        : checkContent,
      locales.getString(`checkType.${poaType}`),
      locales.getString(`checkOrigin.${poaType}`),
      isSelfHosted
        ? locales.getString(`checkQuality.${poaType}.selfHosted`)
        : locales.getString(`checkQuality.${poaType}.hosted`),
    ];

    const isIncomeTaxAfterFirstSeptember =
      poaType === 'INCOME_TAX_NOTICE' && !isBeforeSeptember;

    const isIncomeTaxBeforeFirstSeptember =
      poaType === 'INCOME_TAX_NOTICE' && isBeforeSeptember;

    return (
      <Layout
        data-testid="proof-of-address-upload"
        nav={<OBNavigation onPrev={onPrev} />}
      >
        <Box paddingBottom="space-48">
          <Header
            marginBottom="space-26"
            title={
              isIncomeTaxAfterFirstSeptember
                ? locales.residenceTax
                : locales.title[poaType]
            }
          />

          {isIncomeTaxBeforeFirstSeptember ? (
            <Text>{locales.subtitle}</Text>
          ) : null}

          <Box marginTop="space-40" marginX="space-8">
            <OBList>
              {list.map(
                (text) =>
                  text && (
                    <OBList.Item alignItems="center" key={text}>
                      <SunshineIcon
                        color="grey.800"
                        name="tick"
                        size="icon-16"
                      />

                      <Text fontWeight="weight-400" variant="primary">
                        {text}
                      </Text>
                    </OBList.Item>
                  ),
              )}
            </OBList>
          </Box>

          <Button
            as={chakra.a}
            href={HELP_PAGE_URL}
            marginBottom="space-40"
            marginTop="space-16"
            target="_blank"
            variant="inline-primary"
          >
            {locales.helpLink}
            <SunshineIcon marginLeft="space-8" name="information" />
          </Button>

          <VerificationDocumentUpload
            onSuccessfulUpload={async ({ bucketName, fileRef, url }) => {
              onSuccessfulUpload({
                firebaseBucket: bucketName,
                firebaseRef: fileRef,
                type: isSelfHosted
                  ? KycReviewDocumentType.ProofOfAddress
                  : KycReviewDocumentType.ProofOfAddressHost,
                url,
              });
            }}
          />
        </Box>
      </Layout>
    );
  },
);

export default ProofOfAddressUpload;
